import React, { useState, useEffect } from "react";
import "./single-blog.css";
import Navbar from "../components/Navbar";
import HlcFooter from "../components/HlcFooter";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { API_BASE_URL, getImageUrl } from "../config/apiConfig";



export default function SingleBlogPage() {
  const location = useLocation();

  const postId = location.state?.postId;
  const navigate = useNavigate();

  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // ── Load all posts from adminhlc-api (MySQL) — Step 3 of the Firebase->PHP migration ──
  useEffect(() => {
    let cancelled = false;

    fetch(`${API_BASE_URL}/api/public/blogs/list.php`)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return;

        if (!Array.isArray(data) || data.length === 0) {
          setLoading(false);
          setNotFound(true);
          return;
        }

        const parsed = data
          .map((p) => ({ ...p, image: getImageUrl(p.image) }))
          .filter((p) => p.published)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

        setAllPosts(parsed);

        // Find current post
        let current = null;
        if (postId) {
          current = parsed.find((p) => p.id === postId);
        }
        if (!current) current = parsed[0]; // fallback to latest

        if (!current) { setNotFound(true); setLoading(false); return; }

        setPost(current);

        // Related: same category, exclude current
        const related = parsed
          .filter((p) => p.id !== current.id && p.category === current.category)
          .slice(0, 3);

        // If not enough related, fill with other posts
        if (related.length < 3) {
          const others = parsed
            .filter((p) => p.id !== current.id && !related.find((r) => r.id === p.id))
            .slice(0, 3 - related.length);
          setRelatedPosts([...related, ...others]);
        } else {
          setRelatedPosts(related);
        }

        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch blog posts:", err);
        if (!cancelled) {
          setLoading(false);
          setNotFound(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [postId]);

  // ── Scroll to top on load ───────────────────────────────────────────────────
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [postId]);

  // ── Table of contents: extract h2 headings from content ─────────────────────
  const getTOC = (content) => {
    if (!content) return [];
    const matches = [...content.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi)];
    return matches.map((m, i) => ({ id: `section-${i}`, label: m[1].replace(/<[^>]+>/g, "") }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="hlc-single-loading">
          <div className="hlc-spinner"></div>
          <p>Loading article...</p>
        </div>
        <HlcFooter />
      </>
    );
  }

  if (notFound || !post) {
    return (
      <>
        <Navbar />
        <div className="hlc-single-notfound">
          <h2>Post not found</h2>
          <p>The article you're looking for doesn't exist or has been removed.</p>
          <button onClick={() => navigate("/blog")} className="blog-btn">← Back to Blog</button>
        </div>
        <HlcFooter />
      </>
    );
  }

  const toc = getTOC(post.content);

  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <>
      <Navbar />
      <section className="hlc-single-blog" id="blog-led-cost-saving">
        <div className="hlc-single-inner">

          {/* ── Breadcrumb ── */}
          <nav className="blog-breadcrumb">
            <button onClick={() => navigate("/")}>Home</button>
            <span>/</span>
            <button onClick={() => navigate("/blog")}>Blog</button>
            <span>/</span>
            <strong>{post.title}</strong>
          </nav>

          {/* ── Hero header ── */}
          <header className="single-blog-hero">
            <span className="blog-category">{post.category}</span>
            <h1>{post.title}</h1>
            <p>{post.excerpt}</p>
            <div className="single-meta">
              <span>By {post.author || "HLC Lighting"}</span>
              <span>{post.category}</span>
              <span>{post.readTime || "5 min read"}</span>
            </div>
          </header>

          {/* ── Feature image ── */}
          {post.image && (
            <figure className="single-feature-img">
              <img src={post.image} alt={post.title} />
            </figure>
          )}

          {/* ── Main layout ── */}
          <div className="single-blog-layout">

            {/* ── Article ── */}
            <article className="single-article">
              {post.excerpt && (
                <p className="article-lead">{post.excerpt}</p>
              )}

              {/* Render full content — HTML or plain text */}
              {post.content ? (
                post.content.trim().startsWith("<")
                  ? (
                    <div
                      className="single-article-body"
                      dangerouslySetInnerHTML={{ __html: post.content }}
                    />
                  )
                  : (
                    <div className="single-article-body">
                      {post.content.split("\n").map((para, i) =>
                        para.trim() ? <p key={i}>{para}</p> : null
                      )}
                    </div>
                  )
              ) : (
                <p className="article-lead" style={{ opacity: 0.5 }}>No content provided.</p>
              )}

              {/* ── CTA ── */}
              <div className="article-cta">
                <h3>Want to Estimate Your Savings?</h3>
                <p>Use HLC's lighting savings calculator and see how much you can save monthly and yearly.</p>
                <Link to="/calculator">Calculate Savings</Link>
              </div>
            </article>

            {/* ── Sidebar ── */}
            <aside className="single-sidebar">

              {/* Table of contents — auto-generated from content */}
              {toc.length > 0 && (
                <div className="side-card">
                  <h4>Table of Contents</h4>
                  {toc.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        const el = document.getElementById(item.id);
                        if (el) el.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              )}

              {/* Fallback TOC when no h2 tags */}
              {toc.length === 0 && (
                <div className="side-card">
                  <h4>Table of Contents</h4>
                  <a
                    href="#top"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                  >
                    Back to Top
                  </a>
                </div>
              )}

              <div className="side-card">
                <h4>Need Help Choosing LEDs?</h4>
                <p>Our team can help you choose the right LED products for your home, shop or project.</p>
                <Link to="/contact" className="side-btn">Contact HLC</Link>
              </div>

              {/* Other posts in same category */}
              {allPosts.filter((p) => p.id !== post.id && p.category === post.category).length > 0 && (
                <div className="side-card">
                  <h4>More in {post.category}</h4>
                  {allPosts
                    .filter((p) => p.id !== post.id && p.category === post.category)
                    .slice(0, 3)
                    .map((p) => (
                      <button
                        key={p.id}
                        className="sidebar-related-link"
                        onClick={() => navigate("/single-blog", { state: { postId: p.id } })}
                      >
                        {p.title}
                      </button>
                    ))}
                </div>
              )}

            </aside>

          </div>

          {/* ── Related posts ── */}
          {relatedPosts.length > 0 && (
            <section className="related-posts">
              <h2>Related Articles</h2>
              <div className="related-grid">
                {relatedPosts.map((rPost) => (
                  <article key={rPost.id}>
                    <img
                      src={rPost.image || "https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=600&q=80"}
                      alt={rPost.title}
                    />
                    <h3>{rPost.title}</h3>
                    <button onClick={() => navigate("/single-blog", { state: { postId: rPost.id } })}>
                      Read More
                    </button>
                  </article>
                ))}
              </div>
            </section>
          )}

        </div>
      </section>
      <HlcFooter />
    </>
  );
}